import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import useMediaQuery from '@mui/material/useMediaQuery';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { api, apiEvents, calendarBaseRoute, IEvent } from '../../types';
import { createApiHeaders } from '../../utilities/JwtTokenParser';
import Header from '../../components/Header';
import { useAlert } from '../../layout/MainAlert';

const eventPrototype : IEvent = {
  id: 0, 
  subject: "",
  location: "", 
  description: "",
  startDateTime: new Date(),
  endDateTime: new Date()
}

const saveEventData = async (event: IEvent): Promise<IEvent> => {
  const response = await fetch(`${api}${apiEvents}${event.id === 0 ? "" : event.id}`, {
    method: event.id === 0 ? 'POST' : 'PUT',
    headers: createApiHeaders(true, true),
    body: JSON.stringify(event)
  });

  if (response.ok) {
    const json = await response.json();
    return json;
  }

  throw new Error(`Detailed Error Code: ${response.status}.`);
}

const validationSchema = yup.object().shape({
  subject: yup.string().required("Required. Please, provide subject."),
  startDateTime: yup.date().default(() => new Date()), 
  endDateTime: yup.date().when(
    "startDateTime", 
    (startDateTime, schema) => {
      const startDateTimeAsDate: null|Date = startDateTime && startDateTime.length > 0 ? startDateTime[0] as Date : null;

      // endDateTime must be at least 10 minutes after startDateTime.
      const minValue: null|Date = startDateTimeAsDate && new Date(startDateTimeAsDate.getTime() + 10 * 60000);
      return startDateTime && schema.min(minValue);
    })
});

const CalendarEdit = () => {
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const alert = useAlert();
  const params = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();

  const eventId = params.id;
  const [event, setEvent] = useState<IEvent>(eventPrototype);

  useEffect(() => {
    if (eventId && parseInt(eventId) > 0) { 
      setEvent(state as IEvent);
    }
  }, []);

  const formik = useFormik({
    initialValues: {
      ...event,
      startDateTime: dayjs(event.startDateTime), 
      endDateTime: dayjs(event.endDateTime)
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values) => {
      saveEventData({
        ...values,
        startDateTime: values.startDateTime.toDate(),
        endDateTime: values.endDateTime.toDate()
      })
      .then((response) => {
        alert.success(`Event "${response.subject}" saved successfully.`);
        navigate(`${calendarBaseRoute}`);
      })
      .catch((error) => {
        alert.error(`Event can't be saved. ${error}`);
      });
    }
  });
  
  return (
    <Box m="14px">
      <Header title="EVENT INFO" subtitle="" />

      <form onSubmit={formik.handleSubmit}>
        <Box 
          display="grid" 
          gridTemplateColumns="repeat(6, minmax(0, 1fr))"
          gap="20px" 
          sx={{
            "& > div": { gridColumn: isNonMobile ? undefined : "span 6" }
          }}
        >
          <TextField
            fullWidth
            variant="filled"
            id="subject"
            name="subject"
            label="Subject"
            color="info"
            value={formik.values.subject}
            onChange={formik.handleChange} 
            onBlur={formik.handleBlur}
            error={formik.touched.subject && Boolean(formik.errors.subject)}
            helperText={formik.touched.subject && formik.errors.subject}
            sx={{ gridColumn: "span 6" }}
          />
          
          <TextField 
            fullWidth
            variant="filled"
            id="location"
            name="location"
            label="Location"
            color="info"
            value={formik.values.location}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.location && Boolean(formik.errors.location)}
            helperText={formik.touched.location && formik.errors.location}
            sx={{ gridColumn: "span 6" }}
          />

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimePicker 
              name="startDateTime" 
              label="Start Date Time" 
              value={formik.values.startDateTime} 
              onChange={(value) => {
                setEvent({
                  ...formik.values,
                  startDateTime: value?.toDate() ?? formik.values.startDateTime.toDate(),
                  endDateTime: formik.values.endDateTime.toDate()
                });
              }}
              slotProps={{ 
                textField: { 
                  fullWidth: true,
                  variant: "filled",
                  color: "info",
                  error: formik.touched.startDateTime && Boolean(formik.errors.startDateTime)
                }
              }}
              sx={{ gridColumn: "span 3" }}
            />
          </LocalizationProvider>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimePicker
              name="endDateTime" 
              label="End Date Time"
              value={formik.values.endDateTime}
              onChange={(value) => {
                setEvent({
                  ...formik.values,
                  startDateTime: formik.values.startDateTime.toDate(),  
                  endDateTime: value?.toDate() ?? formik.values.endDateTime.toDate()
                });
              }}
              slotProps={{ 
                textField: { 
                  fullWidth: true,
                  variant: "filled",
                  color: "info",
                  error: formik.touched.endDateTime && Boolean(formik.errors.endDateTime),
                  helperText: formik.touched.endDateTime && formik.errors.endDateTime ? "End Date Time must be at least 10 minutes after Start Date Time" : "" 
                }
              }}
              sx={{ gridColumn: "span 3" }}
            />
          </LocalizationProvider>

          <TextField
            fullWidth
            variant="filled"
            id="description"
            name="description"
            label="Description"
            color="info"
            multiline={true}
            rows={7}
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.description && Boolean(formik.errors.description)}
            helperText={formik.touched.description && formik.errors.description}
            sx={{ gridColumn: "span 6" }}
          />
        </Box>
            
        <Box display="flex" justifyContent="end" mt="20px">
          <Button 
            type="submit" 
            variant="contained" 
            color="secondary"
            sx={{
              fontSize: "12px",
              fontWeight: "bold",
              padding: "8px 14px",
            }} 
          >
            Save Changes
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default CalendarEdit;